// ANCLA: COMPONENT START — ViewStockDispatchPopup

import React, { useEffect, useState } from "react";
import axios from "../../../api/axiosInstance";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import useStockOperationAccess from "../useStockOperationAccess";
import StockOperationsDialog from "./StockOperationsDialog";
import { useDocumentDialog } from "./PurchasingDocumentDialog";
import { stockReturnUrl } from "../tabs/stockReturnMessages";
import { getStockPopupTranslator } from "./stockPopupMessages";
import {
  formatStockOpsCurrency,
  formatStockOpsDate,
  formatStockOpsNumber,
  truncateStockOpsText,
} from "../stockOperationsUi";

export default function ViewStockDispatchPopup({ dispatchId, businessUnitId, onClose }) {
  const access = useStockOperationAccess(businessUnitId);
  const { languageId } = usePmsTheme();
  const t = getStockPopupTranslator(languageId);
  const dialog = useDocumentDialog();
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [dispatch, setDispatch] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true); setDispatch(null); setLoadError("");
    const load = async () => {
      try {
        console.log("[SDN][VIEW][LOAD] Loading dispatch note", dispatchId);

        const res = await axios.get(
          `/api/purchasing/stock-dispatch-notes/${dispatchId}`, access.requestConfig("dispatched")
        );

        if (active && access.can("dispatched", "view", true)) setDispatch(res.data);
      } catch (err) {
        console.error("[SDN][VIEW][ERROR]", err);
        if (active) setLoadError("Error loading dispatch note details.");
      } finally {
        if (active) setLoading(false);
      }
    };

    if (dispatchId) load();
    return () => { active = false; };
  }, [dispatchId, businessUnitId, access]);

  if (!dispatchId) return null;
  if (loading || !dispatch) return <StockOperationsDialog title={t("Dispatch Note Detail")} onClose={onClose}><div className="so-modalBody"><h2 className="so-modalTitle">{t("Dispatch Note Detail")}</h2><p role={loadError ? "alert" : "status"}>{t(loadError || "Loading...")}</p><div className="so-modalFooter"><button type="button" className="so-btn so-btn--ghost" onClick={onClose}>{t("Close")}</button></div></div></StockOperationsDialog>;

  const allLines = dispatch.all_lines || [];
  const totalCost = allLines.reduce(
    (sum, line) => sum + Number(line.total_cost || 0),
    0
  );

  return (
    <StockOperationsDialog title={t("Dispatch Note Detail")} onClose={onClose}>
        <div className="so-modalBody">
          <div className="so-modalHeader">
            <div>
              <div className="so-modalKicker">{t("Stock Dispatched")}</div>
              <h2 className="so-modalTitle">{t("Dispatch Note Detail")}</h2>
              <p className="so-modalSubtitle">
                {t("Review the hierarchy, requested quantities and final dispatch cost for this stock movement.")}
              </p>
            </div>

            <div className="so-modalBadge">{dispatch.dispatch_code}</div>
          </div>

          <div className="so-summaryBar">
            <div className="so-summaryItem">
              <div className="so-summaryItemLabel">{t("Source location")}</div>
              <div className="so-summaryItemValue">{dispatch.requisition?.source_location?.name || dispatch.source_location?.name || "-"}</div>
            </div>
            <div className="so-summaryItem">
              <div className="so-summaryItemLabel">{t("Dispatch Date")}</div>
              <div className="so-summaryItemValue">{formatStockOpsDate(dispatch.dispatch_date)}</div>
            </div>
            <div className="so-summaryItem">
              <div className="so-summaryItemLabel">{t("Requisition")}</div>
              <div className="so-summaryItemValue">{dispatch.requisition?.requisition_code || "-"}</div>
            </div>
            <div className="so-summaryItem">
              <div className="so-summaryItemLabel">{t("Created By")}</div>
              <div className="so-summaryItemValue" title={dispatch.created_by || "-"}>
                {truncateStockOpsText(dispatch.created_by || "-", 40)}
              </div>
            </div>
            <div className="so-summaryItem">
              <div className="so-summaryItemLabel">{t("Total Cost")}</div>
              <div className="so-summaryItemValue">{formatStockOpsCurrency(totalCost)}</div>
            </div>
          </div>

          <div className="so-tableWrap so-tableWrap--modal">
            <table className="so-table">
              <thead>
                <tr>
                  <th>{t("Product Group")}</th>
                  <th>{t("Item")}</th>
                  <th className="so-number">{t("Qty Requested")}</th>
                  <th className="so-number">{t("Pending")}</th>
                  <th className="so-number">{t("Qty Dispatched")}</th>
                  <th className="so-number">{t("Returned quantity")}</th>
                  <th className="so-number">{t("Net consumption")}</th>
                  <th className="so-number">{t("Unit Cost")}</th>
                  <th className="so-number">{t("Total Cost")}</th>
                </tr>
              </thead>

              <tbody>
                {allLines.length ? (
                  allLines.map((line, index) => {
                    const hierarchy = [
                      line.item?.category?.subfamily?.family?.name,
                      line.item?.category?.subfamily?.name,
                      line.item?.category?.name,
                      line.item?.subcategory?.name,
                    ]
                      .filter(Boolean)
                      .join(" / ");

                    return (
                      <tr key={index}>
                        <td className="so-muted" title={hierarchy || "-"}>
                          <span className="so-ellipsis" style={{ maxWidth: "360px" }}>
                            {hierarchy || "-"}
                          </span>
                        </td>
                        <td className="so-strong" title={line.item?.name || "-"}>
                          <span className="so-ellipsis" style={{ maxWidth: "260px" }}>
                            {line.item?.name || "-"}
                          </span>
                        </td>
                        <td className="so-number">{formatStockOpsNumber(line.qty_requested)}</td>
                        <td className="so-number">{formatStockOpsNumber(line.pending_total)}</td>
                        <td className="so-number">{formatStockOpsNumber(line.qty_dispatched)}</td>
                        <td className="so-number">{formatStockOpsNumber(line.qty_returned || 0)}</td>
                        <td className="so-number">{formatStockOpsNumber(line.net_consumption ?? Number(line.qty_dispatched || 0) - Number(line.qty_returned || 0))}</td>
                        <td className="so-number">{formatStockOpsCurrency(line.unit_cost)}</td>
                        <td className="so-number">{formatStockOpsCurrency(line.total_cost)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="9" className="so-empty">
                      {t("No dispatch lines are available for this note.")}
                    </td>
                  </tr>
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td colSpan="8" className="so-number">{t("Total Cost")}</td>
                  <td className="so-number">{formatStockOpsCurrency(totalCost)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="so-modalFooter">
            {String(dispatch.status).toLowerCase() === "confirmed" && access.can("returns", "create") && <a className="so-btn so-btn--primary" href={stockReturnUrl(businessUnitId, dispatchId)}>{t("New return")}</a>}
            <button type="button" className="so-btn so-btn--ghost" onClick={onClose}>
              {t("Close")}
            </button>
          </div>
        </div>
      {dialog.dialog}
    </StockOperationsDialog>
  );
}
